import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, FolderOpen, LayoutGrid, List, KanbanSquare, CalendarRange } from 'lucide-react'
import { workspaceApi, taskApi, pipelineApi, projectApi } from '@/lib/api'
import { useAuthStore } from '@/stores/auth-store'
import { useToast } from '@/stores/toast-store'
import { TaskCard } from '@/components/ui/task-card'
import { TaskListView }     from '@/components/views/task-list-view'
import { TaskKanbanView }   from '@/components/views/task-kanban-view'
import { TaskTimelineView } from '@/components/views/task-timeline-view'
import { Skeleton } from '@/components/ui/skeleton'
import { EmptyState } from '@/components/ui/empty-state'
import { MODULE_REGISTRY } from '@/lib/module-registry'
import type { TaskPriority, ModuleId, ModuleDataMap } from '@/types/task'
import type { ProjectModulesResponse } from '@/types/project'

const PRIORITY_OPTIONS: { value: TaskPriority | ''; label: string }[] = [
  { value: '', label: 'Semua Prioritas' },
  { value: 'urgent', label: 'Mendesak' },
  { value: 'high', label: 'Tinggi' },
  { value: 'medium', label: 'Sedang' },
  { value: 'low', label: 'Rendah' },
]

const ALL_MODULE_IDS = Object.keys(MODULE_REGISTRY) as ModuleId[]

export default function ProjectDetailPage() {
  const { slug, projectId } = useParams<{ slug: string; projectId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const currentUserId = useAuthStore((s) => s.user?.id)

  const [activeTab, setActiveTab] = useState<'tasks' | 'modules'>('tasks')
  const [statusFilter, setStatusFilter] = useState('')
  const [priorityFilter, setPriorityFilter] = useState<TaskPriority | ''>('')
  const [viewMode, setViewMode] = useState<'card' | 'list' | 'kanban' | 'timeline'>('card')
  const [projectModuleDraft, setProjectModuleDraft] = useState<ModuleDataMap>({})
  const [activeModuleTab, setActiveModuleTab] = useState<ModuleId | null>(null)
  const [draftInitialized, setDraftInitialized] = useState(false)

  const modulesQueryKey = ['workspace', slug, 'project', projectId, 'modules'] as const

  const { data: project, isLoading: projectLoading } = useQuery({
    queryKey: ['workspace', slug, 'project', projectId],
    queryFn: () => projectApi.getProject(slug!, projectId!),
    enabled: !!slug && !!projectId,
    staleTime: 60_000,
  })

  const { data: members } = useQuery({
    queryKey: ['workspace', slug, 'members'],
    queryFn: () => workspaceApi.getMembers(slug!),
    enabled: !!slug,
  })

  const { data: statuses } = useQuery({
    queryKey: ['workspace', slug, 'pipeline'],
    queryFn: () => pipelineApi.getStatuses(slug!),
    enabled: !!slug,
  })

  const {
    data: tasks,
    isLoading: tasksLoading,
    error,
  } = useQuery({
    queryKey: [
      'workspace', slug, 'project', projectId, 'tasks',
      { status: statusFilter, priority: priorityFilter },
    ],
    queryFn: () =>
      taskApi.getTasks(slug!, {
        project: projectId,
        status: statusFilter || undefined,
        priority: (priorityFilter as TaskPriority) || undefined,
      }),
    enabled: !!slug && !!projectId,
    staleTime: 30_000,
  })

  const { data: modulesData, isLoading: modulesLoading } = useQuery({
    queryKey: modulesQueryKey,
    queryFn: () => projectApi.getProjectModules(slug!, projectId!),
    enabled: !!slug && !!projectId && activeTab === 'modules',
    staleTime: 60_000,
  })

  // Prefer dedicated modules query; fall back to project.active_modules before tab is visited
  const activeModules = modulesData?.active_modules ?? project?.active_modules ?? []

  // Seed draft sekali saat modulesData pertama kali tersedia
  useEffect(() => {
    if (modulesData && !draftInitialized) {
      setProjectModuleDraft(modulesData.module_data ?? {})
      setDraftInitialized(true)
    }
  }, [modulesData, draftInitialized])

  // Sync activeModuleTab: auto-select pertama jika belum ada, atau jika modul aktif di-toggle off
  useEffect(() => {
    if (activeTab === 'modules') {
      if (activeModuleTab && !activeModules.includes(activeModuleTab)) {
        setActiveModuleTab(activeModules[0] ?? null)
      } else if (!activeModuleTab && activeModules.length > 0) {
        setActiveModuleTab(activeModules[0])
      }
    }
  }, [activeModules, activeTab, activeModuleTab])

  const moveTaskStatus = useMutation({
    mutationFn: ({ taskId, statusId }: { taskId: string; statusId: string | null }) =>
      taskApi.updateTask(slug!, taskId, { status_id: statusId }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['workspace', slug, 'project', projectId, 'tasks'],
      })
    },
    onError: () => {
      toast({ title: 'Gagal memindahkan tugas', variant: 'destructive' })
      void queryClient.invalidateQueries({
        queryKey: ['workspace', slug, 'project', projectId, 'tasks'],
      })
    },
  })

  const toggleModule = useMutation({
    mutationFn: (newModules: ModuleId[]) =>
      projectApi.updateProjectModules(slug!, projectId!, { active_modules: newModules }),
    onMutate: async (newModules) => {
      await queryClient.cancelQueries({ queryKey: modulesQueryKey })
      const prev = queryClient.getQueryData<ProjectModulesResponse>(modulesQueryKey)
      queryClient.setQueryData<ProjectModulesResponse>(modulesQueryKey, (old) => ({
        project_id: projectId!,
        active_modules: newModules,
        module_data: old?.module_data ?? null,
      }))
      return { prev }
    },
    onSuccess: (data) => {
      // Set data langsung dari respons server — hindari race condition
      // di mana GET refetch mengembalikan data lama dari KV cache backend
      queryClient.setQueryData<ProjectModulesResponse>(modulesQueryKey, data)
    },
    onError: (_err, _vars, ctx) => {
      queryClient.setQueryData(modulesQueryKey, ctx?.prev)
      toast({ title: 'Gagal memperbarui modul', variant: 'destructive' })
    },
  })

  const saveProjectModuleData = useMutation({
    mutationFn: (data: ModuleDataMap) =>
      projectApi.updateProjectModules(slug!, projectId!, { module_data: data }),
    onSuccess: (data) => {
      queryClient.setQueryData<ProjectModulesResponse>(modulesQueryKey, data)
      toast({ title: 'Data modul project disimpan' })
    },
    onError: () => {
      toast({ title: 'Gagal menyimpan data modul', variant: 'destructive' })
    },
  })

  function handleToggleModule(moduleId: ModuleId, checked: boolean) {
    const next = checked
      ? [...activeModules, moduleId]
      : activeModules.filter((m) => m !== moduleId)
    toggleModule.mutate(next)
  }

  const myMember = members?.find((m) => m.user_id === currentUserId)
  const myRole = myMember?.role
  const canCreate = myRole !== 'viewer'
  const canManageModules = myRole === 'owner' || myRole === 'admin'
  const isTasksLoading = projectLoading || tasksLoading
  const hasActiveModules = activeModules.length > 0

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-sm text-muted-foreground overflow-hidden">
        <Link to={`/workspaces/${slug}/projects`} className="shrink-0 hover:text-foreground">
          Projects
        </Link>
        <span className="shrink-0">/</span>
        {projectLoading ? (
          <span className="h-4 w-24 animate-pulse rounded bg-secondary" />
        ) : (
          <span className="truncate text-foreground font-medium max-w-[240px]">
            {project?.name ?? 'Project'}
          </span>
        )}
      </nav>

      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          {projectLoading ? (
            <Skeleton className="h-8 w-48" />
          ) : (
            <>
              <div className="flex items-center gap-2">
                <FolderOpen className="h-5 w-5 shrink-0 text-primary" />
                <h1 className="text-2xl font-bold tracking-tight truncate">{project?.name ?? 'Project'}</h1>
              </div>
              {project?.description && (
                <p className="mt-1 text-sm text-muted-foreground">{project.description}</p>
              )}
            </>
          )}
        </div>
        {canCreate && activeTab === 'tasks' && (
          <button
            onClick={() => navigate(`/workspaces/${slug}/projects/${projectId}/tasks/new`)}
            className="hidden shrink-0 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary-hover md:block"
          >
            + Buat Tugas
          </button>
        )}
      </div>

      {/* Tab navigation */}
      <div className="border-b border-border">
        <div className="flex gap-1">
          <button
            onClick={() => setActiveTab('tasks')}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'tasks'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Tugas
          </button>
          <button
            onClick={() => setActiveTab('modules')}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'modules'
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Modul
            {hasActiveModules && (
              <span className="text-xs leading-none text-primary" aria-hidden>●</span>
            )}
          </button>
        </div>
      </div>

      {/* ── Tasks tab ──────────────────────────────────────────────────────────── */}
      {activeTab === 'tasks' && (
        <>
          {/* Filters + View Toggle */}
          <div className="flex flex-wrap items-center gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">Semua Status</option>
              {statuses?.map((s) => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>

            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value as TaskPriority | '')}
              className="rounded-md border border-input bg-background px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {PRIORITY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>

            {(statusFilter || priorityFilter) && (
              <button
                onClick={() => { setStatusFilter(''); setPriorityFilter('') }}
                className="text-xs text-muted-foreground underline hover:text-foreground"
              >
                Reset filter
              </button>
            )}

            {/* View toggle — pushed to the right */}
            <div className="ml-auto flex items-center gap-0.5 rounded-lg border border-border bg-background p-0.5">
              {(
                [
                  { mode: 'card',     Icon: LayoutGrid,    label: 'Kartu'    },
                  { mode: 'list',     Icon: List,          label: 'List'     },
                  { mode: 'kanban',   Icon: KanbanSquare,  label: 'Kanban'   },
                  { mode: 'timeline', Icon: CalendarRange, label: 'Timeline' },
                ] as const
              ).map(({ mode, Icon, label }) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setViewMode(mode)}
                  title={label}
                  aria-label={label}
                  aria-pressed={viewMode === mode}
                  className={`flex h-7 w-7 items-center justify-center rounded-md transition-colors ${
                    viewMode === mode
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-secondary hover:text-foreground'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                </button>
              ))}
            </div>
          </div>

          {/* Loading skeleton */}
          {isTasksLoading && (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-28" />
              ))}
            </div>
          )}

          {/* Error */}
          {error && (
            <p className="text-sm text-destructive">Gagal memuat tugas. Coba refresh halaman.</p>
          )}

          {/* Task views */}
          {!isTasksLoading && !error && tasks && (
            <>
              {tasks.length === 0 ? (
                <EmptyState
                  title={
                    statusFilter || priorityFilter
                      ? 'Tidak ada tugas yang cocok dengan filter.'
                      : 'Belum ada tugas di project ini.'
                  }
                  description={
                    !statusFilter && !priorityFilter
                      ? 'Mulai dengan membuat tugas pertama.'
                      : undefined
                  }
                  action={
                    canCreate && !statusFilter && !priorityFilter ? (
                      <Link
                        to={`/workspaces/${slug}/projects/${projectId}/tasks/new`}
                        className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary-hover"
                      >
                        <Plus className="h-4 w-4" />
                        Buat Tugas Pertama
                      </Link>
                    ) : undefined
                  }
                />
              ) : (
                <>
                  {viewMode === 'card' && (
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                      {tasks.map((task) => (
                        <TaskCard
                          key={task.id}
                          task={task}
                          onClick={() =>
                            navigate(`/workspaces/${slug}/projects/${projectId}/tasks/${task.id}`)
                          }
                        />
                      ))}
                    </div>
                  )}

                  {viewMode === 'list' && (
                    <TaskListView
                      tasks={tasks}
                      onTaskClick={(id) =>
                        navigate(`/workspaces/${slug}/projects/${projectId}/tasks/${id}`)
                      }
                    />
                  )}

                  {viewMode === 'kanban' && (
                    <TaskKanbanView
                      tasks={tasks}
                      statuses={statuses ?? []}
                      onTaskClick={(id) =>
                        navigate(`/workspaces/${slug}/projects/${projectId}/tasks/${id}`)
                      }
                      onStatusChange={(taskId, newStatusId) =>
                        moveTaskStatus.mutate({ taskId, statusId: newStatusId })
                      }
                    />
                  )}

                  {viewMode === 'timeline' && (
                    <TaskTimelineView
                      tasks={tasks}
                      onTaskClick={(id) =>
                        navigate(`/workspaces/${slug}/projects/${projectId}/tasks/${id}`)
                      }
                    />
                  )}
                </>
              )}
            </>
          )}
        </>
      )}

      {/* ── Modules tab ────────────────────────────────────────────────────────── */}
      {activeTab === 'modules' && (
        <div className="space-y-4">
          <div>
            <h2 className="text-base font-semibold tracking-tight">Modul Project</h2>
            <p className="mt-0.5 text-sm text-muted-foreground">
              Aktifkan modul untuk menambahkan field tambahan ke setiap tugas dalam project ini.
            </p>
          </div>

          {/* Module catalog */}
          {modulesLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-20 rounded-xl" />
              ))}
            </div>
          ) : (
            <div className="divide-y divide-border rounded-xl border border-border bg-card">
              {ALL_MODULE_IDS.map((id) => {
                const def = MODULE_REGISTRY[id]
                const isActive = activeModules.includes(id)
                return (
                  <div key={id} className="flex items-center justify-between gap-4 px-4 py-4">
                    <div className="flex min-w-0 items-start gap-3">
                      <span className="mt-0.5 text-xl leading-none">{def.icon}</span>
                      <div className="min-w-0">
                        <p className="text-sm font-medium">{def.label}</p>
                        <p className="mt-0.5 text-xs text-muted-foreground">{def.description}</p>
                      </div>
                    </div>

                    {canManageModules ? (
                      /* Toggle switch */
                      <button
                        type="button"
                        role="switch"
                        aria-checked={isActive}
                        aria-label={`${isActive ? 'Nonaktifkan' : 'Aktifkan'} ${def.label}`}
                        onClick={() => handleToggleModule(id, !isActive)}
                        disabled={toggleModule.isPending}
                        className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:opacity-50 ${
                          isActive ? 'bg-primary' : 'bg-input'
                        }`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition-transform ${
                            isActive ? 'translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    ) : (
                      /* Read-only badge */
                      <span
                        className={`shrink-0 text-xs font-medium ${
                          isActive ? 'text-primary' : 'text-muted-foreground'
                        }`}
                      >
                        {isActive ? '● Aktif' : 'Nonaktif'}
                      </span>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {/* Data safety notice */}
          <p className="text-xs text-muted-foreground">
            ⚠️ Menonaktifkan modul menyembunyikan field dari form tugas. Data yang sudah tersimpan tidak akan dihapus.
          </p>

          {/* Project-level module data */}
          {activeModules.length > 0 && (
            <div className="space-y-4 pt-2">
              <div>
                <h3 className="text-sm font-semibold tracking-tight">Data Modul Project</h3>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  Nilai default di level project. Bisa di-override per tugas.
                </p>
              </div>

              {/* Sub-tab navigation per modul aktif */}
              <div className="border-b border-border">
                <div className="flex gap-1">
                  {activeModules.map((id) => {
                    const def = MODULE_REGISTRY[id]
                    return (
                      <button
                        key={id}
                        type="button"
                        onClick={() => setActiveModuleTab(id)}
                        className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                          activeModuleTab === id
                            ? 'border-primary text-primary'
                            : 'border-transparent text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        <span className="text-base leading-none">{def.icon}</span>
                        {def.label}
                      </button>
                    )
                  })}
                </div>
              </div>

              {/* Form modul yang sedang aktif */}
              {activeModuleTab && (() => {
                const def = MODULE_REGISTRY[activeModuleTab]
                const ModuleComponent = def.component
                return (
                  <div className="rounded-xl border border-border bg-card px-4 py-4">
                    <ModuleComponent
                      moduleId={activeModuleTab}
                      value={projectModuleDraft[activeModuleTab]}
                      onChange={(val: ModuleDataMap[ModuleId]) =>
                        setProjectModuleDraft((prev) => ({ ...prev, [activeModuleTab]: val }))
                      }
                      readOnly={!canManageModules}
                    />
                  </div>
                )
              })()}

              {canManageModules && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => saveProjectModuleData.mutate(projectModuleDraft)}
                    disabled={saveProjectModuleData.isPending}
                    className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary-hover disabled:opacity-50"
                  >
                    {saveProjectModuleData.isPending ? 'Menyimpan…' : 'Simpan'}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Mobile FAB — tasks tab only */}
      {canCreate && activeTab === 'tasks' && (
        <Link
          to={`/workspaces/${slug}/projects/${projectId}/tasks/new`}
          aria-label="Buat tugas baru"
          className="fixed bottom-6 right-6 z-30 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary-hover md:hidden"
        >
          <Plus className="h-6 w-6" />
        </Link>
      )}
    </div>
  )
}
